import type { Feature } from '../../../shared/types';
import type { FeatureAsk } from '../data/store';
import { input } from './components';
import { h, replaceChildren } from './dom';

/**
 * Shared "requested feature" picker (M4): chips of known features (from the mirror, so it works
 * offline) + an inline "new feature" input; per-selection optional note. Used by the quick-log
 * form and the lead detail screen.
 */
export interface FeaturePicker {
  el: HTMLElement;
  getAsks: () => FeatureAsk[];
}

export const featurePicker = (known: readonly Feature[]): FeaturePicker => {
  const selected = new Set<string>(); // feature ids
  const notes = new Map<string, string>(); // feature id | '~new:<name>' → note
  const newNames: string[] = [];

  const notesHost = h('div', { class: 'objdetails' });
  const renderNotes = () => {
    const rows: HTMLElement[] = [];
    for (const id of selected) {
      const feature = known.find((f) => f.id === id);
      rows.push(
        input({
          placeholder: `${feature?.name ?? id} — context (optional)`,
          value: notes.get(id) ?? '',
          maxlength: 1000,
          class: 'input--sm',
          onInput: (v) => notes.set(id, v),
        }),
      );
    }
    for (const name of newNames) {
      rows.push(
        input({
          placeholder: `${name} — context (optional)`,
          value: notes.get(`~new:${name}`) ?? '',
          maxlength: 1000,
          class: 'input--sm',
          onInput: (v) => notes.set(`~new:${name}`, v),
        }),
      );
    }
    replaceChildren(notesHost, rows);
  };

  const chipsHost = h('div', { class: 'chips' });
  const renderChips = () => {
    const chips: HTMLElement[] = [];
    // launched features are not offered, but one picked by typing its name stays visible and removable
    const shown = known.filter((f) => f.status === 'missing' || selected.has(f.id));
    for (const f of shown) {
      chips.push(
        h(
          'button',
          {
            type: 'button',
            class: 'chip',
            'aria-pressed': String(selected.has(f.id)),
            onClick: () => {
              if (selected.has(f.id)) selected.delete(f.id);
              else selected.add(f.id);
              renderChips();
              renderNotes();
            },
          },
          f.name,
        ),
      );
    }
    for (const name of newNames) {
      chips.push(
        h(
          'button',
          {
            type: 'button',
            class: 'chip',
            'aria-pressed': 'true',
            title: 'Tap to remove',
            onClick: () => {
              newNames.splice(newNames.indexOf(name), 1);
              notes.delete(`~new:${name}`);
              renderChips();
              renderNotes();
            },
          },
          `+ ${name}`,
        ),
      );
    }
    replaceChildren(chipsHost, chips);
  };

  const newInput = input({
    placeholder: 'Other feature they asked for…',
    maxlength: 80,
    class: 'input--sm',
    enterkeyhint: 'done',
    onInput: () => undefined,
  });
  const addNew = () => {
    const name = newInput.value.trim();
    if (!name) return;
    const dupKnown = known.find((f) => f.name.toLowerCase() === name.toLowerCase());
    if (dupKnown) {
      selected.add(dupKnown.id);
    } else if (!newNames.some((n) => n.toLowerCase() === name.toLowerCase())) {
      newNames.push(name);
    }
    newInput.value = '';
    renderChips();
    renderNotes();
  };
  newInput.addEventListener('keydown', (ev) => {
    if ((ev as KeyboardEvent).key === 'Enter') {
      ev.preventDefault();
      addNew();
    }
  });
  const addBtn = h('button', { type: 'button', class: 'chip chip--pick', onClick: addNew }, 'Add');

  renderChips();
  const el = h(
    'div',
    { class: 'featurepicker' },
    chipsHost,
    h('div', { class: 'featurepicker__new' }, newInput, addBtn),
    notesHost,
  );

  return {
    el,
    getAsks: () => [
      ...[...selected].map((id) => ({ feature_id: id, notes: notes.get(id) ?? null })),
      ...newNames.map((name) => ({ name, notes: notes.get(`~new:${name}`) ?? null })),
    ],
  };
};
